import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { useMemo } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { FREE } from 'self-service-api/constants/ProductLevels';
import { getPrimaryTracker } from 'usage-tracker-container';
import LoadingScreen from '../components/LoadingScreen';
import { useCompanyDetails } from '../hooks/useCompanyDetails';
import { useRecommendedRouting } from '../hooks/useRecommendations';
import { useAppSelector } from '../reducers';
import { isAppAuthSelector } from '../selectors/AuthSelectors';
import { isUngatedForM1HubPagesSelector, isUngatedForM2BundlePagesSelector, isUngatedForSeatsSelector } from '../selectors/GatingSelectors';
import { ResponsivePageWrapper } from '../v2/ResponsivePageWrapper';
import { ENTERPRISE, tabIdToProductLevels } from '../v2/constants/Tabs';
import { useIsUngatedForPublicRemoveTabs, useIsUngatedForV2PublicHubPages } from '../v2/hooks/gating/useIsUngated';
import { useCrmSuiteOwnershipLevel, useIsFreePortal } from '../v2/hooks/inventory/useProductInventory';
import { CommerceHubPageV2, ContentHubPageV2, CustomerPlatformPageV2, DataHubPageV2, MarketingHubPageV2, ProductPageV1, SalesHubPageV2, ServiceHubPageV2 } from './loadablePages';
import { jsx as _jsx } from "react/jsx-runtime";
export const VersionedPageRoute = ({
  V1Component,
  V2Component,
  isV2Enabled,
  navItem,
  productLine
}) => {
  const PageComponent = isV2Enabled ? V2Component : V1Component;
  return /*#__PURE__*/_jsx(ResponsivePageWrapper, {
    navItem: navItem,
    productLine: productLine,
    children: /*#__PURE__*/_jsx(PageComponent, {})
  });
};
const V2_PAGE_MAP = {
  MARKETING: MarketingHubPageV2,
  SALES: SalesHubPageV2,
  SERVICE: ServiceHubPageV2,
  CMS: ContentHubPageV2,
  OPERATIONS: DataHubPageV2,
  COMMERCE: CommerceHubPageV2
};
export const createV2PricingPageComponent = productLine => {
  const V2PricingPageComponent = () => {
    if (productLine === 'SUITE') {
      return /*#__PURE__*/_jsx(ResponsivePageWrapper, {
        navItem: "suite",
        children: /*#__PURE__*/_jsx(CustomerPlatformPageV2, {})
      });
    }
    const PageComponent = V2_PAGE_MAP[productLine];
    if (!PageComponent) return null;
    return /*#__PURE__*/_jsx(ResponsivePageWrapper, {
      productLine: productLine,
      children: /*#__PURE__*/_jsx(PageComponent, {})
    });
  };
  return V2PricingPageComponent;
};
export const createV1PricingPageComponent = (Component, productLine) => {
  const V1PricingPageComponent = () => /*#__PURE__*/_jsx(ResponsivePageWrapper, {
    productLine: productLine,
    children: /*#__PURE__*/_jsx(Component, {
      productLine: productLine
    })
  });
  return V1PricingPageComponent;
};
export const createTabComponent = (Component, productLine, path, isUngatedForPublicRemoveTabs, isV2) => {
  const TabComponent = ({
    match: {
      params: {
        tab
      }
    },
    location
  }) => {
    if (isUngatedForPublicRemoveTabs) {
      if (tab) {
        return /*#__PURE__*/_jsx(Redirect, {
          to: {
            pathname: path,
            search: location.search
          }
        });
      }
      if (isV2) {
        return /*#__PURE__*/_jsx(Component, {
          productLine: productLine
        });
      }
      return /*#__PURE__*/_jsx(ResponsivePageWrapper, {
        productLine: productLine,
        children: /*#__PURE__*/_jsx(Component, {
          productLine: productLine
        })
      });
    }
    if (!tab || !Object.keys(tabIdToProductLevels).includes(tab)) {
      getPrimaryTracker().setProperties({
        defaultedTab: ENTERPRISE
      });
      return /*#__PURE__*/_jsx(Redirect, {
        to: createToObject(`${path}/${ENTERPRISE}`)
      });
    }
    if (isV2) {
      return /*#__PURE__*/_jsx(Component, {
        productLine: productLine
      });
    }
    return /*#__PURE__*/_jsx(ResponsivePageWrapper, {
      productLine: productLine,
      children: /*#__PURE__*/_jsx(Component, {
        productLine: productLine
      })
    });
  };
  return TabComponent;
};
export const PathRedirect = ({
  from,
  to
}) => /*#__PURE__*/_jsx(Route, {
  path: from,
  render: ({
    location
  }) => {
    return /*#__PURE__*/_jsx(Redirect, {
      to: Object.assign({}, location, {
        pathname: location.pathname.replace(from, to)
      })
    });
  }
});
const InAppProductPageRoute = ({
  path,
  Component = ProductPageV1,
  productLine
}) => {
  const isUngatedForM1HubPages = useAppSelector(isUngatedForM1HubPagesSelector);
  const isUngatedForM2BundlePages = useAppSelector(isUngatedForM2BundlePagesSelector);
  const V2Component = useMemo(() => {
    if (isUngatedForM1HubPages && productLine) {
      return createV2PricingPageComponent(productLine);
    }
    if (path === '/suite' && isUngatedForM2BundlePages) {
      return createV2PricingPageComponent('SUITE');
    }
    return null;
  }, [isUngatedForM1HubPages, isUngatedForM2BundlePages, path, productLine]);
  if (V2Component) {
    return /*#__PURE__*/_jsx(Route, {
      path: path,
      component: V2Component
    });
  }
  const V1Component = createV1PricingPageComponent(Component, productLine);
  return /*#__PURE__*/_jsx(Route, {
    path: path,
    component: V1Component
  });
};
const PublicProductPageRoute = ({
  path,
  Component = ProductPageV1,
  productLine
}) => {
  const isUngatedForV2PublicHubPages = useIsUngatedForV2PublicHubPages();
  const isUngatedForPublicRemoveTabs = useIsUngatedForPublicRemoveTabs();
  const V2Component = useMemo(() => {
    if (isUngatedForV2PublicHubPages && productLine) {
      return createV2PricingPageComponent(productLine);
    }
    if (path === '/suite' && isUngatedForV2PublicHubPages) {
      return createV2PricingPageComponent('SUITE');
    }
    return null;
  }, [isUngatedForV2PublicHubPages, productLine, path]);
  const isV2 = V2Component !== null;
  const ComponentToWrap = V2Component || Component;
  const TabComponent = useMemo(() => createTabComponent(ComponentToWrap, productLine, path, isUngatedForPublicRemoveTabs, isV2), [ComponentToWrap, productLine, path, isUngatedForPublicRemoveTabs, isV2]);

  // Commerce doesn't have tabs, so skip the tab routing logic entirely
  if (productLine === 'COMMERCE') {
    if (isV2) {
      return /*#__PURE__*/_jsx(Route, {
        path: path,
        component: ComponentToWrap
      });
    }
    return /*#__PURE__*/_jsx(Route, {
      path: path,
      render: () => /*#__PURE__*/_jsx(ResponsivePageWrapper, {
        productLine: 'COMMERCE',
        children: /*#__PURE__*/_jsx(Component, {
          productLine: 'COMMERCE'
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(Route, {
    path: `${path}/:tab?`,
    component: TabComponent
  });
};
export const ProductPageRoute = props => {
  const isAppAuth = useAppSelector(isAppAuthSelector);
  if (isAppAuth) {
    return /*#__PURE__*/_jsx(InAppProductPageRoute, Object.assign({}, props));
  }
  return /*#__PURE__*/_jsx(PublicProductPageRoute, Object.assign({}, props));
};
export const DefaultInAppRoute = () => {
  const {
    recommendedRoute,
    loading: isLoadingRecommendedRoute
  } = useRecommendedRouting();
  const {
    loading: isCompanyDetailsLoading,
    isVSBPortal
  } = useCompanyDetails();
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const {
    crmSuiteLevel: ownedSuiteProductLevel
  } = useCrmSuiteOwnershipLevel();
  const {
    isFreePortal,
    loading: isInventoryLoading
  } = useIsFreePortal();
  const isFreeVSBPortal = isVSBPortal && isFreePortal;
  const ownsSuiteProduct = isUngatedForSeats && ownedSuiteProductLevel && ownedSuiteProductLevel !== FREE;
  if (isLoadingRecommendedRoute || isCompanyDetailsLoading || isInventoryLoading) {
    return /*#__PURE__*/_jsx(LoadingScreen, {});
  }
  if (isFreeVSBPortal) {
    return /*#__PURE__*/_jsx(Redirect, {
      from: "/",
      exact: true,
      to: createToObject('/crm')
    });
  }
  if (ownsSuiteProduct) {
    return /*#__PURE__*/_jsx(Redirect, {
      from: "/",
      exact: true,
      to: createToObject('/suite')
    });
  }
  return /*#__PURE__*/_jsx(Redirect, {
    from: "/",
    exact: true,
    to: createToObject(`/${recommendedRoute}`)
  });
};